import { useEffect, useSyncExternalStore } from 'react';
import { createLogger } from '@sorrem/shared';
import { getAssetPrecache, type AssetPrecacheLauncher, type AssetPrecacheState } from '@sorrem-client/ui/assets/asset-precache';
import AssetPrecacheWorker from '../assets/asset-precache-worker-entry?worker';

const logger = createLogger('client:use-asset-precache');

/**
 * Runs the precache in its own worker (#355), which closes once it is done.
 * A worker that can't start or fails counts as done, so entering never
 * waits on it: every file still loads on first use.
 */
const launchPrecacheWorker: AssetPrecacheLauncher = (report) => {
    let worker: Worker;
    try {
        worker = new AssetPrecacheWorker();
    } catch (error) {
        logger.warn('asset precache worker did not start; assets load on first use:', error);
        report({ phase: 'done', done: 0, total: 0 });
        return;
    }
    worker.onmessage = (event: MessageEvent<AssetPrecacheState>): void => {
        report(event.data);
        if (event.data.phase === 'done') {
            worker.terminate();
        }
    };
    worker.onerror = (event): void => {
        logger.warn('asset precache worker failed; assets load on first use:', event.message);
        worker.terminate();
        report({ phase: 'done', done: 0, total: 0 });
    };
};

/**
 * The page's asset precaching (#285), started by the first screen that
 * asks for it (Character Home, entering Sorrem) and shared by every screen
 * after: the core tier while the player waits to enter, then the run tier
 * in the background, which reports only that it started and finished.
 */
export function useAssetPrecache(): AssetPrecacheState {
    const precache = getAssetPrecache();
    useEffect(() => {
        precache.start(launchPrecacheWorker);
    }, [precache]);
    return useSyncExternalStore(precache.subscribe, precache.getState);
}

/** The player-facing line for the core tier being cached: "Caching 42 of 310 assets". */
export function formatAssetPrecache(state: AssetPrecacheState): string {
    return `Caching ${state.done} of ${state.total} assets`;
}
